import React, { useEffect, useState } from "react";
import { createRoot, Root } from "react-dom/client";
import i18n from "i18next";
import { I18nextProvider, useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { getTranslatedKey, showPrice } from "../../../utils/commonfunction";
import { expandByQuantity } from "../../../utils/addonQuantity";
import SetMenuAddonPicker, {
  AddonRowValue,
  SpiceOption,
  seedAddonRows,
} from "../../qrOrder/components/SetMenuAddonPicker";
import SpiceLevelChips from "../../qrOrder/components/SpiceLevelChips";
import { calculateSetMenuPrice } from "../utils/setMenuAddons";
import { CashierGroup, toPickerGroups } from "../utils/setPickerRows";

const translated = (key: string, data: unknown): string =>
  getTranslatedKey(key, data as never);

/**
 * What is being configured and what it currently costs, at the top of either
 * dialog: the cashier opened it from a grid of look-alike tiles, so the name is
 * the confirmation that the right one is open, and the price beside it is the
 * running line total — the same arrangement the qr sheet uses.
 */
const DialogItemHead = ({
  item,
  total,
}: {
  item: Record<string, unknown>;
  /** The running line total. Defaults to the item's own price, which is what
   *  the line costs before any component is taken. */
  total?: number;
}) => (
  <div className="swal-set-item-head">
    <span className="swal-set-item-name">{translated("item_name", item)}</span>
    <span className="swal-set-item-total">
      ¥{showPrice(total ?? (Number(item?.total_price) || 0))}
    </span>
  </div>
);

/**
 * SweetAlert owns the confirm button, so it is written to rather than rendered.
 * Both dialogs say the same thing on it — what adding costs, and whether it can
 * be added yet — so both drive it through here.
 */
const useConfirmButton = (label: string, total: number, blocked: boolean) => {
  useEffect(() => {
    const confirm = Swal.getConfirmButton();
    if (!confirm) {
      return;
    }
    confirm.disabled = blocked;
    confirm.textContent = `${label} : ¥ ${showPrice(total)}`;
    // SweetAlert's own styling ignores `:disabled`, so the fade is ours to do —
    // the brand fill stays, only its strength drops.
    confirm.style.opacity = blocked ? "0.5" : "";
    confirm.style.cursor = blocked ? "not-allowed" : "";
  }, [label, total, blocked]);
};

/**
 * The ids of every option the catalogue currently marks sold out.
 *
 * Checked against the groups, not the rows: a row carries only what is
 * price-affecting, and an option can go sold out while the dialog is open. The
 * order path refuses a sold-out component, so the picker has to as well.
 */
export const soldOutIdsOf = (groups: CashierGroup[] = []): Set<string> =>
  new Set(
    (groups || []).flatMap((group) =>
      (group?.items || [])
        .filter((option) => (option as { is_available?: boolean })?.is_available === false)
        .map((option) => String((option as { _id?: string })?._id))
    )
  );

/** Any chosen component that has since sold out. Blocks the add. */
export const hasSoldOutRow = (rows: AddonRowValue[] = [], soldOutIds: Set<string>): boolean =>
  (rows || []).some((row) => row?.item_id && soldOutIds.has(String(row.item_id)));

interface PickerBodyProps {
  groups: ReturnType<typeof toPickerGroups>;
  /** Ids the catalogue marks sold out, computed once by the opener. */
  soldOutIds: Set<string>;
  spiceOptions: SpiceOption[];
  item: Record<string, unknown>;
  taxRoundType: unknown;
  withSpice: boolean;
  /** Hands every change back out to the dialog, which holds the latest rows. */
  onRows: (rows: AddonRowValue[]) => void;
}

/**
 * The picker plus the running line total, as the SweetAlert's body.
 *
 * The rows live here rather than in the dialog closure so the tree re-renders
 * on every step; the dialog is told about each list so `preConfirm` can read the
 * final one without reaching into the DOM.
 */
const PickerBody = ({
  groups,
  soldOutIds,
  spiceOptions,
  item,
  taxRoundType,
  withSpice,
  onRows,
}: PickerBodyProps) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<AddonRowValue[]>(() => seedAddonRows(groups));

  useEffect(() => {
    onRows(rows);
  }, [rows, onRows]);

  const priced = calculateSetMenuPrice(
    item,
    expandByQuantity(rows.filter((row) => row?.item_id)),
    taxRoundType
  );

  // An empty row is a group still short of its components — the same rule the
  // picker uses to keep one there. A row on an option that has since sold out
  // is the other thing the order path refuses.
  useConfirmButton(
    t("link_add"),
    priced.total_price,
    rows.some((row) => !row?.item_id) || hasSoldOutRow(rows, soldOutIds)
  );

  return (
    <>
      <DialogItemHead item={item} total={priced.total_price} />
      {/* The sections are the one part of this dialog that grows without bound —
          a set can carry more groups, and every group more options, than the
          screen is tall — so they scroll inside their own box and the total
          below stays put. */}
      <div className="swal-set-groups">
        <SetMenuAddonPicker
          groups={groups as never}
          rows={rows}
          spiceOptions={spiceOptions}
          withSpice={withSpice}
          onChangeRows={setRows}
        />
      </div>
    </>
  );
};

interface OpenArgs {
  /** The detail item the combo is priced from. */
  item: Record<string, unknown>;
  /** Choice groups from menu-item-details, keyed by `option_id`. */
  groups: CashierGroup[];
  spiceOptions?: SpiceOption[];
  taxRoundType?: unknown;
  /** Off for bulk settlement: a past sale prints no kitchen ticket. */
  withSpice?: boolean;
  /** Shown when confirm is forced while a group is still short. */
  incompleteMessage: string;
  /** Shown when a chosen option has since sold out. Falls back to the above. */
  soldOutMessage?: string;
  /** Title, buttons and classes stay the caller's — the two differ. */
  swalOptions: Record<string, unknown>;
}

/**
 * Open the set-menu picker as a SweetAlert and resolve with the chosen rows, or
 * null if the cashier backed out.
 *
 * The SAME component the qr diner uses is mounted into the dialog body rather
 * than a second picker being written in html strings. The two used to be
 * separate implementations of one screen, and the cashier's had already fallen
 * behind on look and behaviour; there is now nothing to keep in step.
 */
export const openSetMenuPicker = async ({
  item,
  groups,
  spiceOptions = [],
  taxRoundType,
  withSpice = true,
  incompleteMessage,
  soldOutMessage,
  swalOptions,
}: OpenArgs): Promise<AddonRowValue[] | null> => {
  const container = document.createElement("div");
  let root: Root | null = null;
  // Read at confirm time. `preConfirm` runs outside React, so the latest list is
  // parked here instead of being fished back out of the DOM.
  let latestRows: AddonRowValue[] = [];
  // Computed once, read by both the confirm button and `preConfirm`.
  const soldOutIds = soldOutIdsOf(groups);

  const result = await Swal.fire({
    ...swalOptions,
    html: container,
    didOpen: () => {
      root = createRoot(container);
      // `initReactI18next` is never registered (index.js inits the singleton and
      // passes it down a provider instead), so a tree mounted outside the app's
      // provider has no instance to read and renders raw keys. Same singleton,
      // its own provider.
      root.render(
        <I18nextProvider i18n={i18n}>
          <PickerBody
            groups={toPickerGroups(groups)}
            soldOutIds={soldOutIds}
            spiceOptions={spiceOptions}
            item={item}
            taxRoundType={taxRoundType}
            withSpice={withSpice}
            onRows={(rows) => {
              latestRows = rows;
            }}
          />
        </I18nextProvider>
      );
    },
    // React must come down before SweetAlert drops the container, or the tree
    // is left mounted on a detached node for the life of the page.
    willClose: () => {
      root?.unmount();
      root = null;
    },
    preConfirm: () => {
      if (latestRows.some((row) => !row?.item_id)) {
        Swal.showValidationMessage(incompleteMessage);
        return false;
      }
      // Its own message: "select an item" on a row that HAS one sends the
      // cashier looking in the wrong place.
      if (hasSoldOutRow(latestRows, soldOutIds)) {
        Swal.showValidationMessage(soldOutMessage || incompleteMessage);
        return false;
      }
      return { rows: latestRows };
    },
  } as never);

  const value = (result as { isConfirmed?: boolean; value?: { rows?: AddonRowValue[] } });
  if (!value?.isConfirmed || !value?.value?.rows) {
    return null;
  }
  return value.value.rows;
};

interface SpiceBodyProps {
  item: Record<string, unknown>;
  spiceOptions: SpiceOption[];
  onPick: (spice: SpiceOption | null) => void;
}

/** The plain dialog's body: what is being added, and the level it is added at. */
const SpiceBody = ({ item, spiceOptions, onPick }: SpiceBodyProps) => {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<SpiceOption | null>(null);

  useEffect(() => {
    onPick(chosen);
  }, [chosen, onPick]);

  // Dead until a level has actually been chosen: a tap alone must not add the
  // line, because at a till a mis-tap would go straight onto the order.
  useConfirmButton(t("link_add"), Number(item?.total_price) || 0, !chosen);

  return (
    <div className="set-addon-picker">
      <DialogItemHead item={item} />
      <div className="swal-set-spice-only">
        <span className="set-addon-prompt-title">{t("spice_level_text")}</span>
        <SpiceLevelChips
          name="cashier_spice_dialog"
          options={spiceOptions}
          selectedId={chosen?._id ?? null}
          onPick={setChosen}
        />
      </div>
    </div>
  );
};

interface OpenSpiceArgs {
  /** The item being added — named and described at the top of the dialog. */
  item: Record<string, unknown>;
  spiceOptions: SpiceOption[];
  /** Shown if confirm is forced before a level is chosen. */
  incompleteMessage: string;
  swalOptions: Record<string, unknown>;
}

/**
 * The plain spice dialog: an item that needs a level and has no components.
 *
 * Same chips, same head and same "Add : ¥N" confirm as the set picker — it used
 * to be hand-written radios under an "Are you sure?", which is a different
 * screen for the same question.
 */
export const openSpicePicker = async ({
  item,
  spiceOptions,
  incompleteMessage,
  swalOptions,
}: OpenSpiceArgs): Promise<SpiceOption | null> => {
  const container = document.createElement("div");
  let root: Root | null = null;
  let chosen: SpiceOption | null = null;

  const result = await Swal.fire({
    ...swalOptions,
    html: container,
    didOpen: () => {
      root = createRoot(container);
      root.render(
        <I18nextProvider i18n={i18n}>
          <SpiceBody
            item={item}
            spiceOptions={spiceOptions}
            onPick={(spice) => {
              chosen = spice;
            }}
          />
        </I18nextProvider>
      );
    },
    willClose: () => {
      root?.unmount();
      root = null;
    },
    preConfirm: () => {
      if (!chosen) {
        Swal.showValidationMessage(incompleteMessage);
        return false;
      }
      return { spice: chosen };
    },
  } as never);

  const value = result as { isConfirmed?: boolean };
  return value?.isConfirmed ? chosen : null;
};
